import { supabase, friendlyError } from "../supabase.js";

/* Saving mapped rows into a register.
 *
 * Two ways in, and the difference matters every month after the first.
 *
 *   add    everything becomes a new row. Right the first time.
 *   merge  rows whose own number already exists are updated, the rest are
 *          added. Right every time after that, when somebody drops the same
 *          workbook in again with a fortnight of changes in it.
 *
 * Without merge, the second import of a 389 row action log gives you 778 rows
 * and no way back. With it, dropping the file in each month is the update.
 */

const CHUNK = 200;

/* What a write would do, worked out before anything is written.
 *
 * Separated from the writing so it can be shown to a person and tested
 * without a database. Matching is on one field, normally the team's own
 * numbering, compared as trimmed text because a number in one file and text
 * in another are the same item to everybody except a computer.
 *
 * A value that appears twice in the existing register is not matched at all.
 * That log has two items numbered 1, and picking one of them to overwrite
 * would be a coin toss that silently loses somebody's work. Ambiguity becomes
 * a new row, which is visible, rather than an overwrite, which is not.
 */
export function planWrite(existing = [], incoming = [], matchField = "reference") {
  const key = (v) => String(v ?? "").trim().toLowerCase();

  const seen = new Map();
  const ambiguous = new Set();
  for (const row of existing) {
    const k = key(row[matchField]);
    if (!k) continue;
    if (seen.has(k)) ambiguous.add(k);
    else seen.set(k, row.id);
  }

  const inserts = [];
  const updates = [];
  const duplicated = new Set();
  const usedIncoming = new Set();

  for (const row of incoming) {
    const k = key(row[matchField]);

    /* The same number twice in the file being imported is the same problem
       from the other side: the second one would overwrite the first. */
    if (k && usedIncoming.has(k)) duplicated.add(k);
    if (k) usedIncoming.add(k);

    const id = k && !ambiguous.has(k) && !duplicated.has(k) ? seen.get(k) : undefined;
    if (id) updates.push({ id, row });
    else inserts.push(row);
  }

  return {
    inserts,
    updates,
    ambiguous: [...ambiguous],
    duplicated: [...duplicated],
    unmatched: incoming.filter((r) => !key(r[matchField])).length,
  };
}

const chunks = (list) => {
  const out = [];
  for (let i = 0; i < list.length; i += CHUNK) out.push(list.slice(i, i + CHUNK));
  return out;
};

export async function writeRegister({
  register, contractId, rows, userId = null, mode = "add",
  matchField = "reference", onProgress = () => {},
}) {
  if (!contractId) return { ok: false, error: "No contract is active." };
  if (!rows?.length) return { ok: false, error: "There were no rows to save." };

  let plan = { inserts: rows, updates: [], ambiguous: [], duplicated: [], unmatched: 0 };

  if (mode === "merge") {
    const { data, error } = await supabase
      .from(register).select(`id, ${matchField}`)
      .eq("contract_id", contractId).is("deleted_at", null);
    if (error) return { ok: false, error: friendlyError(error) };
    plan = planWrite(data ?? [], rows, matchField);
  }

  let done = 0;
  const total = plan.inserts.length + plan.updates.length;
  const step = () => { done += 1; onProgress(done, total); };

  for (const batch of chunks(plan.inserts)) {
    const payload = batch.map((r) => ({
      ...r, contract_id: contractId, added_by: userId, updated_by: userId,
    }));
    const { error } = await supabase.from(register).insert(payload);
    if (error) return { ok: false, error: friendlyError(error), saved: done };
    done += batch.length;
    onProgress(done, total);
  }

  /* Updates go one at a time because they are one at a time: each row has its
     own id and its own values. There are rarely many, because most of a
     re-import is rows that have not changed. */
  for (const { id, row } of plan.updates) {
    const { error } = await supabase
      .from(register).update({ ...row, updated_by: userId }).eq("id", id);
    if (error) return { ok: false, error: friendlyError(error), saved: done };
    step();
  }

  return {
    ok: true,
    added: plan.inserts.length,
    updated: plan.updates.length,
    ambiguous: plan.ambiguous,
    duplicated: plan.duplicated,
  };
}
